<template lang="pug">
div
    v-card
        v-card-title Campaign Configuration Settings
            v-spacer
            v-btn(color="primary" @click="openAddDialog") 
                v-icon(left) mdi-plus
                | Add Setting
        
        v-data-table(
            :headers="headers"
            :items="settings"
            :loading="loading"
            loading-text="Loading settings..."
            :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
        )
            template(v-slot:item.setting_value="{ item }")
                v-chip(small :color="getTypeColor(item.setting_type)") {{ formatValue(item) }}

            template(v-slot:item.setting_type="{ item }")
                v-chip(x-small outlined) {{ item.setting_type }}

            template(v-slot:item.actions="{ item }")
                v-btn(icon small color="blue" @click="editSetting(item)" title="Edit")
                    v-icon mdi-pencil
                v-btn(icon small color="red" @click="confirmDelete(item)" title="Delete")
                    v-icon mdi-delete

    v-dialog(v-model="dialog" max-width="600px" persistent)
        v-card
            v-card-title 
                span.headline {{ dialogTitle }}
            v-card-text
                v-form(ref="form" v-model="valid")
                    v-text-field(
                        v-model="editedItem.setting_key"
                        label="Setting Key*"
                        :rules="[rules.required]"
                        :disabled="isEdit"
                        hint="Unique identifier for this setting"
                        persistent-hint
                    )
                    
                    v-select(
                        v-model="editedItem.setting_type"
                        :items="settingTypes"
                        label="Type*"
                        :rules="[rules.required]"
                    )

                    v-text-field(
                        v-if="editedItem.setting_type === 'STRING'"
                        v-model="editedItem.setting_value"
                        label="Value*"
                        :rules="[rules.required]"
                    )

                    v-text-field(
                        v-else-if="editedItem.setting_type === 'NUMBER'"
                        v-model.number="editedItem.setting_value"
                        label="Value*"
                        type="number"
                        :rules="[rules.required, rules.number]"
                    )

                    v-select(
                        v-else-if="editedItem.setting_type === 'BOOLEAN'"
                        v-model="editedItem.setting_value"
                        :items="booleanOptions"
                        label="Value*"
                        :rules="[rules.required]"
                    )

                    v-textarea(
                        v-else-if="editedItem.setting_type === 'JSON'"
                        v-model="editedItem.setting_value"
                        label="Value (JSON)*"
                        :rules="[rules.required, rules.json]"
                        hint="Enter valid JSON"
                        persistent-hint
                    )

                    v-textarea(
                        v-model="editedItem.description"
                        label="Description"
                        rows="3"
                        hint="Optional description for this setting"
                        persistent-hint
                    )

            v-card-actions
                v-spacer
                v-btn(color="grey darken-1" text @click="closeDialog") Cancel
                v-btn(color="primary" @click="saveSetting" :disabled="!valid") Save

    v-dialog(v-model="deleteDialog" max-width="400px")
        v-card
            v-card-title.headline Confirm Delete
            v-card-text Are you sure you want to delete "{{ deleteItem.setting_key }}"?
            v-card-actions
                v-spacer
                v-btn(color="grey darken-1" text @click="deleteDialog = false") Cancel
                v-btn(color="red darken-1" text @click="deleteSetting") Delete

    v-snackbar(top v-model="successMessage" color="success") {{ successText }}
    v-snackbar(top v-model="error.show" color="red") {{ error.message }}
</template>

<script>
export default {
    name: 'CampaignSettings',
    data() {
        return {
            loading: false,
            settings: [],
            dialog: false,
            deleteDialog: false,
            valid: false,
            successMessage: false,
            successText: '',
            error: {
                show: false,
                message: ''
            },
            editedItem: this.getDefaultItem(),
            deleteItem: {},
            isEdit: false,
            settingTypes: ['STRING', 'NUMBER', 'JSON', 'BOOLEAN'],
            booleanOptions: [
                { text: 'True', value: 'true' },
                { text: 'False', value: 'false' }
            ],
            rules: {
                required: value => !!value || value === 0 || 'Required.',
                number: value => !isNaN(parseFloat(value)) || 'Must be a valid number',
                json: value => {
                    try {
                        JSON.parse(value);
                        return true;
                    } catch (e) {
                        return 'Must be valid JSON';
                    }
                }
            },
            headers: [
                {
                    text: 'Setting Key',
                    align: 'start',
                    sortable: true,
                    value: 'setting_key',
                    width: 250
                },
                {
                    text: 'Value',
                    align: 'start',
                    sortable: false,
                    value: 'setting_value',
                    width: 200
                },
                {
                    text: 'Type',
                    align: 'center',
                    sortable: true,
                    value: 'setting_type',
                    width: 120
                },
                {
                    text: 'Description',
                    align: 'start',
                    sortable: false,
                    value: 'description',
                    width: 300
                },
                {
                    text: 'Actions',
                    align: 'end',
                    sortable: false,
                    value: 'actions',
                    width: 120
                }
            ]
        };
    },
    computed: {
        dialogTitle() {
            return this.isEdit ? 'Edit Setting' : 'Add New Setting';
        }
    },
    mounted() {
        this.loadSettings();
        this.initializeDefaultSettings();
    },
    methods: {
        getDefaultItem() {
            return {
                setting_key: '',
                setting_value: '',
                setting_type: 'NUMBER',
                description: ''
            };
        },
        async loadSettings() {
            try {
                this.loading = true;
                const response = await this.$store.dispatch('bh-campaign-settings/find', {
                    query: { $limit: 100 }
                });
                this.settings = response.data || [];
            } catch (error) {
                this.error.message = `Error loading settings: ${error.message}`;
                this.error.show = true;
            } finally {
                this.loading = false;
            }
        },
        async initializeDefaultSettings() {
            try {
                // Initialize default_tokens_per_rating
                const existingTokens = this.settings.find(s => s.setting_key === 'default_tokens_per_rating');
                if (!existingTokens) {
                    await this.$store.dispatch('bh-campaign-settings/create', {
                        setting_key: 'default_tokens_per_rating',
                        setting_value: '10',
                        setting_type: 'NUMBER',
                        description: 'Default number of tokens required per rating point'
                    });
                }

                // Initialize default_campaign_duration_hours
                const existingDuration = this.settings.find(s => s.setting_key === 'default_campaign_duration_hours');
                if (!existingDuration) {
                    await this.$store.dispatch('bh-campaign-settings/create', {
                        setting_key: 'default_campaign_duration_hours',
                        setting_value: '120',
                        setting_type: 'NUMBER',
                        description: 'Default campaign duration in hours (120 hours = 5 days)'
                    });
                }

                // Initialize default_survey_tree_id
                const existingSurvey = this.settings.find(s => s.setting_key === 'default_survey_tree_id');
                if (!existingSurvey) {
                    await this.$store.dispatch('bh-campaign-settings/create', {
                        setting_key: 'default_survey_tree_id',
                        setting_value: '1',
                        setting_type: 'NUMBER',
                        description: 'Default survey tree ID for campaign-serviced songs (5-question set)'
                    });
                }

                // Reload settings if any were created
                if (!existingTokens || !existingDuration || !existingSurvey) {
                    await this.loadSettings();
                }
            } catch (error) {
                console.error('Error initializing default settings:', error);
            }
        },
        openAddDialog() {
            this.isEdit = false;
            this.editedItem = this.getDefaultItem();
            this.dialog = true;
        },
        editSetting(item) {
            this.isEdit = true;
            this.editedItem = { ...item };
            this.dialog = true;
        },
        closeDialog() {
            this.dialog = false;
            this.$refs.form.reset();
            this.editedItem = this.getDefaultItem();
        },
        async saveSetting() {
            if (!this.$refs.form.validate()) return;

            try {
                const data = { ...this.editedItem };
                
                // Convert value to string for storage
                if (data.setting_type === 'NUMBER') {
                    data.setting_value = String(data.setting_value);
                } else if (data.setting_type === 'BOOLEAN') {
                    data.setting_value = String(data.setting_value);
                } else if (data.setting_type === 'JSON') {
                    // Validate and minify JSON
                    JSON.parse(data.setting_value);
                }

                if (this.isEdit) {
                    await this.$store.dispatch('bh-campaign-settings/patch', [data.id, data]);
                    this.successText = 'Setting updated successfully';
                } else {
                    await this.$store.dispatch('bh-campaign-settings/create', data);
                    this.successText = 'Setting created successfully';
                }

                this.successMessage = true;
                this.closeDialog();
                await this.loadSettings();
            } catch (error) {
                this.error.message = `Error saving setting: ${error.message}`;
                this.error.show = true;
            }
        },
        confirmDelete(item) {
            this.deleteItem = item;
            this.deleteDialog = true;
        },
        async deleteSetting() {
            try {
                await this.$store.dispatch('bh-campaign-settings/remove', this.deleteItem.id);
                this.successText = 'Setting deleted successfully';
                this.successMessage = true;
                this.deleteDialog = false;
                await this.loadSettings();
            } catch (error) {
                this.error.message = `Error deleting setting: ${error.message}`;
                this.error.show = true;
            }
        },
        formatValue(item) {
            if (item.setting_type === 'JSON') {
                try {
                    return JSON.stringify(JSON.parse(item.setting_value), null, 0);
                } catch {
                    return item.setting_value;
                }
            }
            return item.setting_value;
        },
        getTypeColor(type) {
            const colors = {
                STRING: 'blue',
                NUMBER: 'green',
                JSON: 'purple',
                BOOLEAN: 'orange'
            };
            return colors[type] || 'grey';
        }
    }
};
</script>

<style scoped>
.v-chip {
    font-family: monospace;
}
</style>
